import React, { FC, useMemo } from "react";
import styled from "styled-components";
import {
  tombac,
  Modal,
  ModalLayout,
  ModalHeader,
  ModalContent,
  ModalActions,
  Text,
  Button,
  Box,
} from "tombac";
import { UserProductSafe } from "./../legolandApi/legolandApiTypes";
import { ProductsApiKeys } from "./ProductsApiKeys";
import { renderProductIcon } from "../../icons";

export type LegolandNavbarApiKeyModalProps = {
  isOpen: boolean;
  products: UserProductSafe[] | undefined;
  setIsOpen: (isOpen: boolean) => void;
};

export const LegolandNavbarApiKeyModal: FC<LegolandNavbarApiKeyModalProps> = ({
  isOpen,
  products,
  setIsOpen,
}) => {
  const productsByApiKey = useMemo(() => {
    return products?.reduce((acc, item) => {
      if (item.access.status !== "GRANTED") return acc;
      const key = item.access.apiKey;
      if (!key) return acc;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {} as Record<string, UserProductSafe[]>);
  }, [products]);

  const productsWithPendingAccess = products?.filter(
    (product) => product.access.status === "PENDING"
  );

  const existProductWithExpiredAccess = products?.some(
    (p) => p.access.status === "EXPIRED"
  );

  const existProductWithApiKey =
    products && productsByApiKey && Object.keys(productsByApiKey).length > 0;
  const existProductWithPendingAccess =
    products &&
    productsWithPendingAccess &&
    productsWithPendingAccess.length > 0;

  return (
    <Modal isOpen={isOpen}>
      <ModalLayout $width="420u">
        <ModalHeader> Your API key</ModalHeader>
        <ModalContent variant="scrollable">
          {products &&
            !existProductWithApiKey &&
            !existProductWithPendingAccess && (
              <Text $pb="3sp">
                {existProductWithExpiredAccess
                  ? "Your access to the products has expired. If you are interested in renewing your license, please contact"
                  : "You currently do not have access to any products. If you are interested in obtaining a license, please contact"}
                <Text
                  as="a"
                  href="mailto:movesupport@tomtom.com"
                  $display="inline-block"
                  variant="interactive"
                >
                  movesupport@tomtom.com
                </Text>
              </Text>
            )}

          {existProductWithApiKey && (
            <ProductsApiKeys products={productsByApiKey} />
          )}
          {existProductWithPendingAccess && (
            <Box $mb="2sp">
              <Text>Pending access:</Text>
              {productsWithPendingAccess.map((p) => (
                <span> {renderProductIcon(p.id)}</span>
              ))}
            </Box>
          )}
          <Text fontWeight="bold">
            Read more about how to use API access on:
          </Text>
          <LinkStyled
            as="a"
            href="https://developer.tomtom.com/documentation#traffic-apis"
          >
            https://developer.tomtom.com/documentation#traffic-apis
          </LinkStyled>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setIsOpen(false)} variant="primary">
            Close
          </Button>
        </ModalActions>
      </ModalLayout>
    </Modal>
  );
};

const LinkStyled = styled(Text)`
  color: ${tombac.color("accent", 600)};
  &:hover {
    cursor: pointer;
  }
`;
